/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { Path as IconPath } from '@ahamove/icons/map';
import { IndexedPath as IconIndexedPath } from '@ahamove/icons/marker';

import type { SpecialRequest } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { FloatingLabelInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface ModalDoorToDoorProps {
  request: SpecialRequest;
  onCloseModal: () => void;
}

export default function DoorToDoor({
  request,
  onCloseModal,
}: ModalDoorToDoorProps) {
  const isMobile = useIsMobile();
  const localeSuffix = useLocaleSuffix();
  const tActions = useTranslations('common.actions');

  const allPaths = useLocationStore.use.paths();
  const dropOffPath = allPaths.slice(1); // Exclude paths[0]
  const { selectedService, setServiceRequest, serviceRequests } =
    useBookingStore((state) => state);

  const updatePathByIndex = useLocationStore.use.updatePathByIndex();
  const [checkedPaths, setCheckedPaths] = useState<number[]>([]);

  const handleCheckboxChange = (index: number) => {
    setCheckedPaths((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const schema = z.object({
    paths: z.array(
      z.object({
        building: z.string().optional(),
      })
    ),
    checkedPaths: z.array(z.number()).optional(),
  });

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      paths: dropOffPath.map((path) => ({
        building: path.building ?? '',
      })),
      checkedPaths: [],
    },
  });

  const onSubmit = (data: FormData) => {
    if (!selectedService?._id) return;

    const param = { ...serviceRequests };

    if (dropOffPath.length === 1) {
      updatePathByIndex(
        {
          ...dropOffPath[0],
          building: data.paths[0].building,
        },
        1
      );
      param[request._id] = [1];
    } else {
      data.checkedPaths?.forEach((checkedIndex) => {
        updatePathByIndex(
          {
            ...dropOffPath[checkedIndex],
            building: data.paths[checkedIndex].building,
          },
          checkedIndex + 1
        );
      });
      param[request._id] = checkedPaths.map((item) => item + 1);
    }

    setServiceRequest(param);
    onCloseModal();
  };

  return (
    <Box p={0}>
      <Text py={rem(16)} c={'neutral.7'} size="sm">
        {request[`description_${localeSuffix}`]}
      </Text>
      <Group gap={4}>
        <Text size="sm" fw={700}>
          Phí áp dụng:
        </Text>
        <Text size="sm" fw={700} c="primary.4">
          {formatCurrency(request.price || 0)} / điểm giao
        </Text>
      </Group>

      <Divider mx={-24} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={isMobile ? rem(16) : rem(8)} py="md">
          <Title pb={rem(8)} order={5}>
            Chọn địa chỉ giao tận tay
          </Title>

          {dropOffPath.length === 1 ? (
            <Controller
              name="paths.0.building"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <Group gap={rem(8)}>
                    <IconPath width={16} height={16} />
                    <Title order={6}>{dropOffPath?.[0]?.first_label}</Title>
                  </Group>
                  <FloatingLabelInput
                    label="Tên tòa nhà, số tầng, số phòng"
                    h="auto"
                    value={value}
                    onChange={(e) => onChange(e)}
                  />
                </>
              )}
            />
          ) : (
            dropOffPath.map((path, index) => (
              <Box key={index}>
                <Button
                  onClick={() => {
                    handleCheckboxChange(index);
                    setValue(
                      'checkedPaths',
                      checkedPaths.includes(index)
                        ? checkedPaths.filter((i) => i !== index)
                        : [...checkedPaths, index]
                    );
                  }}
                  variant="subtle"
                  size="md"
                  px={0}
                  pt="sm"
                  h="auto"
                  color="neutral"
                  fw={500}
                  fullWidth
                  radius={0}
                  justify="space-between"
                  rightSection={
                    <Checkbox checked={checkedPaths.includes(index)} />
                  }
                >
                  <Group gap={rem(8)}>
                    <IconIndexedPath width={16} height={16} value={index + 1} />
                    <Title order={6}>{path.first_label}</Title>
                  </Group>
                </Button>
                {checkedPaths.includes(index) && (
                  <Controller
                    name={`paths.${index}.building`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FloatingLabelInput
                        label="Tên tòa nhà, số tầng, số phòng"
                        h="auto"
                        value={value}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                )}
              </Box>
            ))
          )}
        </Stack>

        <Button
          fullWidth
          size="md"
          mih={rem(48)}
          type="submit"
          disabled={!isValid}
        >
          {tActions('confirm')}
        </Button>
      </form>
    </Box>
  );
}
